import { authConfig } from '../config/auth';

// MyLogin OAuth2 configuration
const MYLOGIN_CONFIG = authConfig.myLogin;

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'mylogin_access_token',
  REFRESH_TOKEN: 'mylogin_refresh_token',
  TOKEN_EXPIRY: 'mylogin_token_expiry',
  USER_DATA: 'mylogin_user_data',
  AUTH_STATE: 'mylogin_auth_state'
};


export const initiateMyLoginAuth = (redirectAfterLogin = null) => {
  if (redirectAfterLogin) {
    localStorage.setItem('mylogin_redirect_after_login', redirectAfterLogin);
  }

  const authState = generateRandomState();
  persistAuthState(authState);

  const queryParams = new URLSearchParams({
    client_id: MYLOGIN_CONFIG.clientId,
    redirect_uri: MYLOGIN_CONFIG.redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    state: authState
  }).toString();

  const authUrl = `${MYLOGIN_CONFIG.url}/oauth/authorize?${queryParams}`;
  
  window.location.href = authUrl;
};

const generateRandomState = () => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const persistAuthState = (state) => {
  // Use sessionStorage for OAuth state to reduce exposure across browser sessions.
  sessionStorage.setItem(STORAGE_KEYS.AUTH_STATE, state);
  // Backward-compat cleanup of old storage location.
  localStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
};

const getStoredAuthState = () => {
  // Fallback to localStorage for in-flight auth attempts created before this change.
  return (
    sessionStorage.getItem(STORAGE_KEYS.AUTH_STATE) ||
    localStorage.getItem(STORAGE_KEYS.AUTH_STATE)
  );
};

const clearStoredAuthState = () => {
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
  localStorage.removeItem(STORAGE_KEYS.AUTH_STATE);
};

export const validateMyLoginAuthState = (returnedState) => {
  if (!returnedState) {
    return false;
  }

  const storedState = getStoredAuthState();
  const isValid = Boolean(storedState) && storedState === returnedState;

  // Always clear state after callback processing to prevent replay attempts.
  clearStoredAuthState();

  return isValid;
};

export const handleMyLoginCallback = async (code, state, options = {}) => {
  console.log("MyLogin callback handling started", { code, state });
  try {
    const { skipStateValidation = false } = options;

    if (!skipStateValidation && !validateMyLoginAuthState(state)) {
      throw new Error('Invalid state parameter. Authentication rejected.');
    }

    // Exchange code for tokens
    console.log('Exchanging code for tokens with redirect URI:', MYLOGIN_CONFIG.redirectUri);
    const tokenResponse = await fetch(`${MYLOGIN_CONFIG.url}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${btoa(`${MYLOGIN_CONFIG.clientId}:${MYLOGIN_CONFIG.secretKey}`)}`
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: MYLOGIN_CONFIG.redirectUri
      })
    });

    const tokens = await tokenResponse.json();
    
    if (!tokenResponse.ok || !tokens.access_token) {
      throw new Error('Failed to exchange code for tokens');
    }

    const expiresAt = Date.now() + (tokens.expires_in * 1000);

    storeAuthTokens(tokens.access_token, tokens.refresh_token, expiresAt);

    // Get user information using the access token
    const userData = await fetchUserData(tokens.access_token);

    return {
      user: userData,
      tokens: {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt
      }
    };
  } catch (error) {
    console.error('MyLogin authentication error:', error);
    throw error;
  }
};

export const fetchUserData = async (accessToken) => {
  const userResponse = await fetch(`${MYLOGIN_CONFIG.url}/api/user`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  const userData = await userResponse.json();
  
  if (!userResponse.ok) {
    throw new Error('Failed to get user information');
  }

  const user = {
    id: userData.data.service_providers.wonde.service_provider_id,
    firstName: userData.data.first_name,
    lastName: userData.data.last_name,
    email: userData.data.email,
    fullName: `${userData.data.first_name} ${userData.data.last_name}`,
  };

  // Store user data
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));

  return user;
};


const storeAuthTokens = (accessToken, refreshToken, expiresAt) => {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiresAt.toString());
};

export const getAccessToken = async () => {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const expiryTime = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  
  // If no token exists, user is not logged in
  if (!accessToken || !refreshToken) {
    return null;
  }

  // Check if token is expired (with a 60-second buffer)
  const isExpired = Date.now() > (parseInt(expiryTime) - 60000);
  
  if (isExpired) {
    try {
      const newTokens = await refreshAccessToken(refreshToken);
      return newTokens.accessToken;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      clearAuthData();
      return null;
    }
  }
  
  return accessToken;
};


export const refreshAccessToken = async (refreshToken) => {
  const tokenResponse = await fetch(`${MYLOGIN_CONFIG.url}/oauth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': `Basic ${btoa(`${MYLOGIN_CONFIG.clientId}:${MYLOGIN_CONFIG.secretKey}`)}`
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    })
  });

  const tokens = await tokenResponse.json();
  
  if (!tokenResponse.ok || !tokens.access_token) {
    throw new Error('Failed to refresh access token');
  }

  const expiresAt = Date.now() + (tokens.expires_in * 1000);
  
  storeAuthTokens(tokens.access_token, tokens.refresh_token || refreshToken, expiresAt);

  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token || refreshToken,
    expiresAt
  };
};

export const isAuthenticated = () => {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const expiryTime = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
  
  if (!accessToken || !expiryTime) {
    return false;
  }
  
  // Check if token is valid
  const isValid = Date.now() < parseInt(expiryTime);
  return isValid;
};

export const getMyLoginUser = () => {
  const userDataString = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (!userDataString) return null;
  
  try {
    return JSON.parse(userDataString);
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
};

export const getUserInfo = () => getMyLoginUser();

export const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

export const logoutFromMyLogin = (redirectAfterLogout = null) => {
  // Clear auth data first
  clearAuthData();
  
  // Construct the logout URL with redirect
  let logoutUrl = `${MYLOGIN_CONFIG.url}/oauth/logout?client_id=${MYLOGIN_CONFIG.clientId}`;
  
  if (redirectAfterLogout) {
    logoutUrl += `&redirect_uri=${encodeURIComponent(redirectAfterLogout)}`;
  }
  
  window.location.href = logoutUrl;
};
